import { Dot, Popover, Tooltip } from "@xola/ui-kit";
import { RowNode } from "ag-grid-community";
import { DateFormat } from "common";
import { sortBy } from "lodash";
import React, { useState } from "react";
import { Note, Purchase, PurchaseItem, User } from "types";
import { useResource } from "../../../../hooks/useResource";
import { CreateCustomerNoteModal } from "../../../Customers/Contacts/CreateCustomerNoteModal";
import { RowData } from "./types";
import { NoTranslate } from "@/components/Common/NoTranslate";

export const sanitizeNoteText = (text: string) => text.replace(/\n+/g, "\n");

const NoteItem = ({ note, onClick }: { note: Note; onClick: () => void }) => {
    const { data: user } = useResource<User>(["users", note.updatedBy?.id], null, {
        // Sometimes, because of some bad data, `note.updatedBy` does not exist. Causing this to throw "TypeError: cannot read "id" of undefined. To prevent calling API in this scenario, `enabled` is added
        enabled: !!note.updatedBy?.id,
    });

    return (
        <li className="no-translate cursor-pointer hover:underline" onClick={onClick}>
            <Tooltip
                placement="right"
                content={
                    <div className="py-1">
                        <div className="text-base">{sanitizeNoteText(note.text)}</div>
                        <div className="mt-2 text-sm text-gray">{note.updated.format(DateFormat.DATE_TIME_SHORT)}</div>
                        {user?.name ? <div className="text-sm text-gray">{user.name}</div> : null}
                    </div>
                }
            >
                <span className="flex items-center space-x-1">
                    <Dot className="flex-shrink-0 bg-gray-darker" />
                    <span className="w-64 truncate">{sanitizeNoteText(note.text)}</span>
                </span>
            </Tooltip>
        </li>
    );
};

interface NotesCellProps {
    notes: Note[];
    purchase: Purchase;
    travelerId?: ID;
    purchaseItem: PurchaseItem;
    isGuestCell?: boolean;
}

export const NotesCell = ({ notes, purchase, travelerId, purchaseItem, isGuestCell = false }: NotesCellProps) => {
    const [selectedNote, setSelectedNote] = useState<Note | null>(null);
    const handleNoteClick = (note: Note) => setSelectedNote(note);
    const handleModalClose = () => setSelectedNote(null);

    return (
        <>
            <ul className="py-2 leading-5">
                {notes.map((note: Note) => (
                    <NoteItem key={note.id} note={note} onClick={() => handleNoteClick(note)} />
                ))}
            </ul>

            {selectedNote && (
                <CreateCustomerNoteModal
                    isOpen={Boolean(selectedNote)}
                    note={selectedNote}
                    heading={travelerId ? "Customer" : "Purchase"}
                    purchaseId={isGuestCell ? undefined : purchase.id}
                    customerId={travelerId ?? purchaseItem?.organizer?.user?.id ?? ""}
                    onClose={handleModalClose}
                />
            )}
        </>
    );
};

export const notesCellValueGetter = ({ data }: { data: RowData }) => {
    if ("purchasesNoteCount" in data) {
        return data.purchasesNoteCount;
    }

    return { notes: data.item.purchase?.notes, isOrganizer: data.isOrganizer, guestNotes: data.guestNotes };
};

export const NotesCellRenderer = ({ data, node }: { data: RowData; node: RowNode }) => {
    // Header Non-Expanded Cell
    if (!node.expanded && node.key === "summary" && "purchasesNoteCount" in data) {
        const totalCount = data.purchasesNoteCount + data.customerNotesCount;

        return (
            <div>
                <Popover appendTo={document.body} disabled={totalCount === 0}>
                    <span className="underline decoration-dotted">
                        <var data-var="totalCount">{totalCount}</var> Notes
                    </span>
                    <Popover.Content>
                        <div className="min-w-[160px] p-3">
                            <ul className="py-2 leading-5">
                                <li className="flex justify-between">
                                    <span>Purchase Notes</span>
                                    <NoTranslate>{data.purchasesNoteCount}</NoTranslate>
                                </li>
                                <li className="flex justify-between">
                                    <span>Customer Notes</span>
                                    <NoTranslate>{data.customerNotesCount}</NoTranslate>
                                </li>
                            </ul>
                        </div>
                    </Popover.Content>
                </Popover>
            </div>
        );
    }

    // Header Expanded Cell
    if ("purchasesNoteCount" in data) {
        return (
            <ul className="py-2 leading-5">
                <li data-testid="roster-guests-list-purchase-notes-counter">
                    <span>
                        <var data-var="purchasenotecount">{data.purchasesNoteCount}</var> Purchase Notes
                    </span>
                </li>
                <li data-testid="roster-guests-list-customer-notes-counter">
                    <span>
                        <var data-var="customernotecount">{data.customerNotesCount}</var> Customer Notes
                    </span>
                </li>
            </ul>
        );
    }

    if (data.isOrganizer) {
        // Organizer Cell
        const sortedNotes = sortBy(data.item.purchase.notes, (note) => note.created.unix());
        return (
            <NotesCell
                purchaseItem={data.item}
                travelerId={data?.travelerId}
                purchase={data.item.purchase}
                notes={sortedNotes}
                isGuestCell={false}
            />
        );
    }

    // Guest row
    if (data?.guestNotes) {
        const sortedNotes = sortBy(data.guestNotes, (note) => note.created.unix());
        return (
            <NotesCell
                isGuestCell
                travelerId={data?.travelerId}
                purchaseItem={data.item}
                purchase={data.item.purchase}
                notes={sortedNotes}
            />
        );
    }

    return null;
};
